import { createApp } from "vue";
import { createPinia } from "pinia";

import App from "./App.vue";
import router, { replaceUnknownLocation } from "./router";
import config from "@/config/env";
// Ext lib
import * as Sentry from "@sentry/vue";
import vuetify from "./plugins/vuetify";
import i18n from "@/config/i18n";
import LoadScript from "vue-plugin-load-script";
// Keycloak
import type { KeycloakOnLoad } from "keycloak-js";
import Keycloak from "keycloak-js";

import { BIconBtnWithTooltip, BButtonIconText } from "@bisom/web_package";
import BTextField from "@/components/bisom/input/BTextField.vue";
import createCable from "@/services/wss";

// CSS
import "@/assets/css/leafleft.css";
import "@/assets/css/base.css";

// récupération de l'idp
const currentUrl = window.location.href;
const isExternalIdp = currentUrl.includes(config.kcIdpEndpoint);
let idpHint = undefined as undefined | string;
let hasAuthIdp = false;

// si nous sommes sur la bonne URL
if (isExternalIdp) {
  const urlParams = new URLSearchParams(window.location.search);
  // Vérifie s'il y a des paramètres
  if (urlParams.toString()) {
    urlParams.forEach((value, key) => {
      if (key === config.kcIdpParams && !!value) {
        idpHint = value;
        hasAuthIdp = true;
      }
    });
  }
}

const keycloakInitParams = hasAuthIdp
  ? { checkLoginIframe: false }
  : {
      onLoad: config.keycloakInitOptions.onLoad as KeycloakOnLoad,
      checkLoginIframe: false,
    };

const keycloak = new Keycloak(config.keycloakInitOptions);

replaceUnknownLocation();

keycloak.init(keycloakInitParams).then((auth: boolean) => {
  if (!auth) {
    if (hasAuthIdp) {
      keycloak.login({ idpHint });
    } else {
      window.location.reload();
    }
  } else if (keycloak.token && keycloak.idToken && keycloak.refreshToken) {
    const urlConnect = `${config.ws_url}cable?access-token=${keycloak.token}&id-token=${keycloak.idToken}`;
    const cable = createCable(urlConnect);

    const app = createApp(App);
    app.config.globalProperties.$cable = cable;

    if (process.env.NODE_ENV !== "development") {
      Sentry.init({
        app,
        ...config.sentry,
        release: "bisom-web-app-v2@" + process.env.VUE_APP_VERSION,
        integrations: [Sentry.browserTracingIntegration()],
        beforeSend(event, hint) {
          const error = hint.originalException as { name: string };
          if (error && error.name === "AxiosError") {
            return null;
          } else {
            return event;
          }
        },
      });
    }

    app
      .use(createPinia())
      .use(router)
      .use(i18n)
      .use(vuetify)
      .use(LoadScript)
      .component("BBtn", BButtonIconText)
      .component("BIconBtn", BIconBtnWithTooltip)
      .component("BTextField", BTextField);

    app.mount("#app");

    localStorage.setItem("vue-token", keycloak.token);
    localStorage.setItem("vue-id-token", keycloak.idToken);
    localStorage.setItem("vue-refresh-token", keycloak.refreshToken);

    // Token Refresh
    setInterval(async () => {
      try {
        const refreshed = await keycloak.updateToken(70);

        if (refreshed && keycloak.token && keycloak.idToken && keycloak.refreshToken) {
          localStorage.setItem("vue-token", keycloak.token);
          localStorage.setItem("vue-id-token", keycloak.idToken);
          localStorage.setItem("vue-refresh-token", keycloak.refreshToken);
        } else {
          // console.log("Token not refreshed, valid for " + Math.round(keycloak.tokenParsed.exp + keycloak.timeSkew - new Date().getTime() / 1000) + " seconds");
        }
      } catch (error) {
        Sentry.captureMessage(`Error refreshing token: ${error}`);
      }
    }, config.refreshTokenTime);
  }
});

export const kc = keycloak;
